import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/api';
import { defaultInventoryAgingCriteria, parseIntervalDaysStrict } from '../../utils/inventoryAgingQuery';

/** One-click bucket presets (each value is the upper day limit of that band). */
const INTERVAL_PRESETS = [
  { label: '30 days only', days: [30] },
  { label: '30 · 60 · 90', days: [30, 60, 90] },
  { label: '30 · 60 · 90 · 120', days: [30, 60, 90, 120] },
  { label: '… + 180', days: [30, 60, 90, 120, 180] },
  { label: '7 · 15 · 30', days: [7, 15, 30] },
];

function fieldCls() {
  return 'border border-sap-border rounded px-2 py-1 text-xs w-full bg-white focus:outline-none focus:border-sap-blue';
}

function labelCls() {
  return 'text-xs text-sap-textLight whitespace-nowrap';
}

function daysBetweenIso(postIso, reportIso) {
  if (!postIso || !reportIso || !/^\d{4}-\d{2}-\d{2}$/.test(postIso)) return NaN;
  const p = new Date(`${postIso}T12:00:00`);
  const r = new Date(`${reportIso}T12:00:00`);
  return Math.floor((r - p) / 86400000);
}

function toYmdFromDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function computeBandRows(intervalDays, reportDate) {
  const rows = [];
  let p = 0;
  const r = new Date(`${reportDate}T12:00:00`);
  const list = intervalDays || [];
  for (let i = 0; i < list.length; i += 1) {
    const hi = parseInt(String(list[i]), 10);
    if (!Number.isFinite(hi) || hi < 1) {
      rows.push({ idx: i, fromIso: '', toIso: '', hi: null, prev: p });
      continue;
    }
    const fromD = new Date(r);
    fromD.setDate(fromD.getDate() - hi);
    const toD = new Date(r);
    toD.setDate(toD.getDate() - (p + 1));
    rows.push({
      idx: i,
      fromIso: toYmdFromDate(fromD),
      toIso: toYmdFromDate(toD),
      hi,
      prev: p,
    });
    p = hi;
  }
  return rows;
}

export default function InventoryAgingCriteriaModal({ open, onClose, onApply, value }) {
  const [form, setForm] = useState(() => ({ ...defaultInventoryAgingCriteria(), ...value }));
  const [itemGroups, setItemGroups] = useState([]);
  const [loadingOpt, setLoadingOpt] = useState(false);
  const [okError, setOkError] = useState('');

  useEffect(() => {
    if (open) {
      setForm({ ...defaultInventoryAgingCriteria(), ...value });
      setOkError('');
      setLoadingOpt(true);
      api
        .get('/inventory/aging/options')
        .then((d) => setItemGroups(Array.isArray(d?.itemGroups) ? d.itemGroups : []))
        .catch(() => setItemGroups([]))
        .finally(() => setLoadingOpt(false));
    }
  }, [open, value]);

  const bandRows = useMemo(
    () => computeBandRows(form.intervalDays, form.reportDate),
    [form.reportDate, form.intervalDays]
  );

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setIntervalDay = (idx, raw) => {
    setOkError('');
    const n = parseInt(String(raw), 10);
    setForm((f) => {
      const next = [...(f.intervalDays || [])];
      next[idx] = Number.isNaN(n) ? '' : n;
      return { ...f, intervalDays: next };
    });
  };

  /** From date → upper day limit for this band (must stay strictly above previous row, below next). */
  const setBandFromDate = (idx, fromIso) => {
    if (!fromIso || !/^\d{4}-\d{2}-\d{2}$/.test(fromIso)) return;
    let hi = daysBetweenIso(fromIso, form.reportDate);
    if (!Number.isFinite(hi) || hi < 1) return;
    setForm((f) => {
      const d = [...(f.intervalDays || [])];
      const prevUpper = idx > 0 ? parseInt(String(d[idx - 1]), 10) : 0;
      if (idx > 0 && Number.isFinite(prevUpper) && hi <= prevUpper) hi = prevUpper + 1;
      const nextUpper = idx + 1 < d.length ? parseInt(String(d[idx + 1]), 10) : NaN;
      if (Number.isFinite(nextUpper) && hi >= nextUpper) hi = nextUpper - 1;
      if (idx > 0 && (!Number.isFinite(hi) || hi <= prevUpper)) return f;
      if (Number.isFinite(nextUpper) && hi >= nextUpper) return f;
      d[idx] = hi;
      return { ...f, intervalDays: d };
    });
  };

  /**
   * To date → adjusts previous row’s upper day limit (youngest age in this band).
   * Row 2+: intervalDays[idx-1] = daysBetween(To, report) - 1.
   */
  const setBandToDate = (idx, toIso) => {
    if (idx < 1 || !toIso || !/^\d{4}-\d{2}-\d{2}$/.test(toIso)) return;
    const a = daysBetweenIso(toIso, form.reportDate);
    if (!Number.isFinite(a) || a < 1) return;
    const newPrevBound = a - 1;
    setForm((f) => {
      const d = [...(f.intervalDays || [])];
      const curHi = parseInt(String(d[idx]), 10);
      if (!Number.isFinite(curHi) || newPrevBound >= curHi) return f;
      if (newPrevBound < 1) return f;
      if (idx >= 2) {
        const prev2 = parseInt(String(d[idx - 2]), 10);
        if (Number.isFinite(prev2) && newPrevBound <= prev2) return f;
      }
      d[idx - 1] = newPrevBound;
      return { ...f, intervalDays: d };
    });
  };

  const addIntervalRow = () => {
    setOkError('');
    setForm((f) => {
      const prev = f.intervalDays || [];
      if (prev.length === 0) return { ...f, intervalDays: [30] };
      const last = parseInt(String(prev[prev.length - 1]), 10) || 30;
      return { ...f, intervalDays: [...prev, last + 30] };
    });
  };

  const removeIntervalRow = (idx) => {
    setOkError('');
    setForm((f) => {
      const next = [...(f.intervalDays || [])];
      next.splice(idx, 1);
      return { ...f, intervalDays: next };
    });
  };

  const applyIntervalPreset = (days) => {
    setOkError('');
    setForm((f) => ({ ...f, intervalDays: [...days] }));
  };

  const handleOk = () => {
    const intervalDays = parseIntervalDaysStrict(form.intervalDays);
    if (!intervalDays.length) {
      setOkError(
        'Add at least one time interval: choose a preset above or click “+ Add interval row”.'
      );
      return;
    }
    if (form.whIncludeOn) {
      const f = String(form.whIncludeFrom || '').trim();
      const t = String(form.whIncludeTo || '').trim();
      if (!f || !t) {
        setOkError(
          'Including warehouses: enter both From and To warehouse codes, or clear “Including”.'
        );
        return;
      }
    }
    if (form.whExcludeOn) {
      const f = String(form.whExcludeFrom || '').trim();
      const t = String(form.whExcludeTo || '').trim();
      if (!f || !t) {
        setOkError(
          'Excluding warehouses: enter both From and To warehouse codes, or clear “Excluding”.'
        );
        return;
      }
    }
    setOkError('');
    onApply({ ...form, intervalDays });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inv-aging-criteria-title"
    >
      <div className="bg-[#E8E8E8] border border-[#B0B0B0] rounded shadow-xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-3 py-2 border-b border-[#B0B0B0] bg-[#DCDCDC]">
          <h2 id="inv-aging-criteria-title" className="text-xs font-bold text-sap-text">
            Inventory Aging Report Filter
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-sap-textLight hover:text-sap-text text-lg leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="overflow-y-auto p-4 text-sap-text">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left column */}
            <div className="space-y-4">
              <div>
                <div className={labelCls()}>Report Date</div>
                <input
                  type="date"
                  className={`${fieldCls()} w-44 mt-0.5`}
                  value={form.reportDate}
                  onChange={(e) => set('reportDate', e.target.value)}
                />
              </div>

              <div>
                <div className="text-xs font-semibold underline decoration-sap-border mb-1">Items</div>
                <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-2 items-center">
                  <span className={labelCls()}>Code From</span>
                  <div className="flex gap-2 items-center">
                    <input
                      className={fieldCls()}
                      value={form.codeFrom}
                      onChange={(e) => set('codeFrom', e.target.value)}
                      placeholder="From"
                    />
                  </div>
                  <span className={labelCls()}>To</span>
                  <input
                    className={fieldCls()}
                    value={form.codeTo}
                    onChange={(e) => set('codeTo', e.target.value)}
                    placeholder="To"
                  />
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className={labelCls()}>Item Group</span>
                  <select
                    className={`${fieldCls()} flex-1 max-w-xs`}
                    value={form.itemGroupId}
                    onChange={(e) => set('itemGroupId', e.target.value)}
                    disabled={loadingOpt}
                  >
                    <option value="All">All</option>
                    {itemGroups.map((g) => (
                      <option key={g.group_id} value={String(g.group_id)}>
                        {g.group_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <button
                    type="button"
                    className="px-2 py-0.5 text-2xs border border-sap-border rounded bg-[#5C5C5C] text-white"
                  >
                    Properties
                  </button>
                  <select
                    className={`${fieldCls()} w-32`}
                    value={form.propertiesMode}
                    onChange={(e) => set('propertiesMode', e.target.value)}
                  >
                    <option value="ignore">Ignore</option>
                    <option value="include">Include</option>
                  </select>
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold underline decoration-sap-border mb-1">Warehouses</div>
                <label className="flex items-center gap-2 text-xs mb-1">
                  <input
                    type="checkbox"
                    checked={form.whIncludeOn}
                    onChange={(e) => set('whIncludeOn', e.target.checked)}
                  />
                  Including
                </label>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xs text-sap-textLight">From</span>
                  <input
                    className={fieldCls()}
                    value={form.whIncludeFrom}
                    onChange={(e) => set('whIncludeFrom', e.target.value)}
                    disabled={!form.whIncludeOn}
                  />
                  <span className="text-2xs text-sap-textLight">To</span>
                  <input
                    className={fieldCls()}
                    value={form.whIncludeTo}
                    onChange={(e) => set('whIncludeTo', e.target.value)}
                    disabled={!form.whIncludeOn}
                  />
                </div>
                <label className="flex items-center gap-2 text-xs mb-1">
                  <input
                    type="checkbox"
                    checked={form.whExcludeOn}
                    onChange={(e) => set('whExcludeOn', e.target.checked)}
                  />
                  Excluding
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-2xs text-sap-textLight">From</span>
                  <input
                    className={fieldCls()}
                    value={form.whExcludeFrom}
                    onChange={(e) => set('whExcludeFrom', e.target.value)}
                    disabled={!form.whExcludeOn}
                  />
                  <span className="text-2xs text-sap-textLight">To</span>
                  <input
                    className={fieldCls()}
                    value={form.whExcludeTo}
                    onChange={(e) => set('whExcludeTo', e.target.value)}
                    disabled={!form.whExcludeOn}
                  />
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="space-y-4">
              <div>
                <div className="text-xs font-semibold mb-1">Issue Strategy</div>
                <label className="flex items-center gap-2 text-xs mr-4">
                  <input
                    type="radio"
                    name="issue_strat"
                    checked={form.issueStrategy === 'lifo'}
                    onChange={() => set('issueStrategy', 'lifo')}
                  />
                  LIFO
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="radio"
                    name="issue_strat"
                    checked={form.issueStrategy === 'fifo'}
                    onChange={() => set('issueStrategy', 'fifo')}
                  />
                  FIFO
                </label>
              </div>

              <div>
                <div className="text-xs font-semibold mb-1">Valuation</div>
                <label className="flex items-center gap-2 text-xs mr-4">
                  <input
                    type="radio"
                    name="valuation"
                    checked={form.valuation === 'document'}
                    onChange={() => set('valuation', 'document')}
                  />
                  Document Price
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="radio"
                    name="valuation"
                    checked={form.valuation === 'current'}
                    onChange={() => set('valuation', 'current')}
                  />
                  Current Price
                </label>
              </div>

              <div>
                <div className="text-xs font-semibold underline decoration-sap-border mb-1">Time Intervals</div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {INTERVAL_PRESETS.map((pr) => (
                    <button
                      key={pr.label}
                      type="button"
                      onClick={() => applyIntervalPreset(pr.days)}
                      className="px-2 py-0.5 text-2xs border border-sap-border rounded bg-white text-sap-text hover:bg-[#E8F0ED] shadow-sm"
                    >
                      {pr.label}
                    </button>
                  ))}
                </div>
                <div className="border border-sap-border rounded bg-white max-h-56 overflow-x-auto overflow-y-auto">
                  <table className="w-full text-2xs border-collapse min-w-[520px]">
                    <colgroup>
                      <col style={{ width: '2rem' }} />
                      <col style={{ width: '6.25rem' }} />
                      <col style={{ minWidth: '11rem' }} />
                      <col style={{ minWidth: '11rem' }} />
                      <col style={{ width: '2.25rem' }} />
                    </colgroup>
                    <thead>
                      <tr className="bg-[#E8F0ED]">
                        <th className="border border-sap-border px-1 py-1">#</th>
                        <th className="border border-sap-border px-1 py-1 whitespace-nowrap">
                          Days (upper limit)
                        </th>
                        <th className="border border-sap-border px-1 py-1">From</th>
                        <th className="border border-sap-border px-1 py-1">To</th>
                        <th className="border border-sap-border px-1 py-1"> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {bandRows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={5}
                            className="border border-sap-border px-3 py-4 text-center text-sap-textLight italic"
                          >
                            No intervals yet — use a preset or “+ Add interval row”.
                          </td>
                        </tr>
                      ) : (
                        bandRows.map((row, i) => (
                          <tr key={row.idx} className={i % 2 === 0 ? 'bg-white' : 'bg-sap-bg/40'}>
                            <td className="border border-sap-border px-1 py-0.5 text-center tabular-nums">
                              {i + 1}
                            </td>
                            <td className="border border-sap-border px-1 py-0.5 align-top">
                              <input
                                type="number"
                                min={1}
                                step={1}
                                className="w-full min-w-[5.25rem] max-w-[6.5rem] box-border border border-sap-border rounded px-2 py-1 text-xs bg-white focus:outline-none focus:border-sap-blue tabular-nums"
                                value={form.intervalDays[row.idx] ?? ''}
                                onChange={(e) => setIntervalDay(row.idx, e.target.value)}
                              />
                            </td>
                            <td className="border border-sap-border px-1 py-0.5 align-top">
                              <input
                                type="date"
                                className="w-full min-w-[10.5rem] box-border border border-sap-border rounded px-1.5 py-1 text-xs bg-white focus:outline-none focus:border-sap-blue"
                                value={row.fromIso || ''}
                                disabled={!row.fromIso}
                                onChange={(e) => setBandFromDate(row.idx, e.target.value)}
                              />
                            </td>
                            <td className="border border-sap-border px-1 py-0.5 align-top">
                              <input
                                type="date"
                                className={`w-full min-w-[10.5rem] box-border border border-sap-border rounded px-1.5 py-1 text-xs focus:outline-none focus:border-sap-blue ${row.idx === 0 ? 'bg-sap-bg/50 cursor-not-allowed' : 'bg-white'}`}
                                value={row.toIso || ''}
                                readOnly={row.idx === 0}
                                title={
                                  row.idx === 0
                                    ? 'First band always starts at age 1 day (day before report date).'
                                    : 'Adjusts the upper limit of the previous row to keep bands contiguous.'
                                }
                                disabled={!row.toIso}
                                onChange={(e) => row.idx > 0 && setBandToDate(row.idx, e.target.value)}
                              />
                            </td>
                            <td className="border border-sap-border px-0.5 py-0.5 text-center align-top">
                              <button
                                type="button"
                                title="Remove row"
                                onClick={() => removeIntervalRow(row.idx)}
                                className="text-sap-textLight hover:text-red-600 px-1 leading-none"
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  onClick={addIntervalRow}
                  className="mt-1 text-2xs text-sap-blue hover:underline"
                >
                  + Add interval row
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 px-4 py-3 border-t border-[#B0B0B0] bg-[#DCDCDC]">
          {okError ? (
            <div className="text-2xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
              {okError}
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleOk}
              className="px-6 py-1.5 text-xs font-semibold bg-sap-blue text-white border border-sap-blueDark rounded hover:opacity-95"
            >
              OK
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-1.5 text-xs font-semibold bg-[#5C5C5C] text-white border border-[#404040] rounded hover:bg-[#4a4a4a]"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
